import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Button } from 'primeng/button';
import { InputNumber } from 'primeng/inputnumber';
import { Guid, isNil, isNotNil, Nillable } from '@web/utils';
import {
  FormControlComponent,
  ImageThumbnailComponent,
  ImageThumbnailUnsupportedComponent,
  NoDataComponent,
  PrimengButtonDirective,
} from '@web/shared/ui';
import { AdminOrderShipmentComponentGroupType } from '../../enums/admin-order-shipment-component-group-type';
import { AdminOrderShipmentFormItem } from '../../models/admin-order-shipment-form-item';
import { AdminOrderShipmentItemSelectModalComponent } from '../admin-order-shipment-item-select-modal/admin-order-shipment-item-select-modal.component';

@Component({
  selector: 'app-admin-order-shipment-items-control',
  imports: [
    Button,
    CommonModule,
    FormControlComponent,
    FormsModule,
    ImageThumbnailComponent,
    ImageThumbnailUnsupportedComponent,
    InputNumber,
    NgOptimizedImage,
    NoDataComponent,
    PrimengButtonDirective,
    AdminOrderShipmentItemSelectModalComponent,
  ],
  templateUrl: './admin-order-shipment-items-control.component.html',
  styleUrl: './admin-order-shipment-items-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdminOrderShipmentItemsControlComponent),
      multi: true,
    },
  ],
})
export class AdminOrderShipmentItemsControlComponent
  implements ControlValueAccessor
{
  public readonly label = input.required<string>();
  public readonly readonlyReason = input<Nillable<string>>(null);
  public readonly type = input.required<AdminOrderShipmentComponentGroupType>();

  protected readonly items = signal<AdminOrderShipmentFormItem[]>([]);
  protected readonly modalVisible = signal(false);
  protected readonly isNotNil = isNotNil;
  protected readonly isReadonly = computed(
    () => this.disabled() || isNotNil(this.readonlyReason()),
  );

  private readonly disabled = signal(false);
  private onChange: (value: AdminOrderShipmentFormItem[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  public writeValue(value: Nillable<AdminOrderShipmentFormItem[]>): void {
    this.items.set(value ?? []);
  }

  public registerOnChange(fn: (value: AdminOrderShipmentFormItem[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected addButtonLabel(): string {
    switch (this.type()) {
      case AdminOrderShipmentComponentGroupType.Cosmetic:
        return 'Dodaj kosmetyki';
      case AdminOrderShipmentComponentGroupType.Box:
        return 'Dodaj boxy kosmetyczne';
      case AdminOrderShipmentComponentGroupType.Product:
        return 'Dodaj produkty';
    }
  }

  protected emptyMessage(): string {
    switch (this.type()) {
      case AdminOrderShipmentComponentGroupType.Cosmetic:
        return 'Brak kosmetyków w przesyłce.';
      case AdminOrderShipmentComponentGroupType.Box:
        return 'Brak boxów kosmetycznych w przesyłce.';
      case AdminOrderShipmentComponentGroupType.Product:
        return 'Brak produktów w przesyłce.';
    }
  }

  protected handleAddClick(): void {
    if (this.isReadonly()) {
      return;
    }

    this.modalVisible.set(true);
    this.onTouched();
  }

  protected handleRemoveClick(index: number): void {
    if (this.isReadonly()) {
      return;
    }

    const next = [...this.items()];
    next.splice(index, 1);
    this.updateItems(next);
  }

  protected handleQuantityChange(
    index: number,
    quantity: Nillable<number>,
  ): void {
    if (this.isReadonly()) {
      return;
    }

    const normalizedQuantity = Math.max(1, Math.trunc(quantity ?? 1));
    const next = this.items().map((item, itemIndex) =>
      itemIndex === index ? item.withQuantity(normalizedQuantity) : item,
    );

    this.updateItems(next);
  }

  protected handleModalCancelEvent(): void {
    this.modalVisible.set(false);
  }

  protected handleModalSubmitEvent(items: AdminOrderShipmentFormItem[]): void {
    const current = this.items();
    const currentIds = new Set(
      current.map((item) => item.id).filter(isNotNil),
    );

    this.updateItems([
      ...current,
      ...items.filter((item) => isNil(item.id) || !currentIds.has(item.id)),
    ]);
    this.modalVisible.set(false);
  }

  protected excludedIds(): Guid[] {
    return this.items().map((item) => item.id).filter(isNotNil);
  }

  private updateItems(items: AdminOrderShipmentFormItem[]): void {
    this.items.set(items);
    this.onChange(items);
    this.onTouched();
  }
}
